import React, { useState, useEffect } from 'react';
import { CloseIcon } from './Icons';
import { useMindspaceStore } from '../../store/mindspaceStore';
import { trackEvent } from '../../services/analyticsService';
import { logDebug, logError } from '../../utils/logging';
import styles from './BusinessProfileModal.module.css';
import { useIsGuest } from '../../store/authStore';

interface BusinessProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  product_service_description: string;
  target_audience_description: string;
  brand_voice: string;
}

interface FormErrors {
  product_service_description?: string;
  target_audience_description?: string;
  brand_voice?: string;
  general?: string;
}

const BusinessProfileModal: React.FC<BusinessProfileModalProps> = ({ isOpen, onClose }) => {
  const { 
    defaultMindspace, 
    contextItems, 
    updateContextItem, 
    getContextValue, 
    error: mindspaceError, 
    clearError 
  } = useMindspaceStore();

  const [formData, setFormData] = useState<FormData>({
    product_service_description: '',
    target_audience_description: '',
    brand_voice: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const isGuest = useIsGuest();

  // Load existing context values when modal opens
  useEffect(() => {
    if (isOpen && defaultMindspace) {
      setFormData({
        product_service_description: getContextValue('product_service_description'),
        target_audience_description: getContextValue('target_audience_description'),
        brand_voice: getContextValue('brand_voice')
      });
      setErrors({});
      clearError();
      
      // Track modal open event
      trackEvent('business_profile_modal_opened', {
        mindspace_id: defaultMindspace.id,
        has_existing_data: contextItems.length > 0
      });
    }
  }, [isOpen, defaultMindspace, getContextValue, contextItems.length, clearError]);

  // Handle form input changes
  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field-specific error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Validate form data
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.product_service_description.trim()) {
      newErrors.product_service_description = 'Descrierea produsului/serviciului este obligatorie';
    } else if (formData.product_service_description.trim().length < 10) {
      newErrors.product_service_description = 'Descrierea trebuie să aibă cel puțin 10 caractere';
    }

    if (!formData.target_audience_description.trim()) {
      newErrors.target_audience_description = 'Descrierea audiență țintă este obligatorie';
    } else if (formData.target_audience_description.trim().length < 10) {
      newErrors.target_audience_description = 'Descrierea trebuie să aibă cel puțin 10 caractere';
    }

    if (!formData.brand_voice.trim()) {
      newErrors.brand_voice = 'Vocea brandului este obligatorie';
    } else if (formData.brand_voice.trim().length < 5) {
      newErrors.brand_voice = 'Vocea brandului trebuie să aibă cel puțin 5 caractere';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !defaultMindspace) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      logDebug('[BusinessProfileModal] Submitting form data:', formData);

      // Update each context item
      const updatePromises = Object.entries(formData).map(([key, value]) => {
        if (value.trim()) {
          return updateContextItem(key, value.trim());
        }
        return Promise.resolve();
      });

      await Promise.all(updatePromises);

      // Show success toast
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);

      // Track successful update
      trackEvent('business_profile_updated', {
        mindspace_id: defaultMindspace.id,
        fields_updated_count: Object.keys(formData).filter(key => formData[key as keyof FormData].trim() !== '').length,
        total_context_items: contextItems.length
      });

      logDebug('[BusinessProfileModal] Profile updated successfully');

      // Close modal after short delay to show success
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      logError('[BusinessProfileModal] Error updating profile:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'A apărut o eroare la salvarea datelor';
      setErrors({ general: errorMessage });

      // Track update failure
      trackEvent('business_profile_update_failed', {
        mindspace_id: defaultMindspace?.id,
        error: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!isSubmitting) {
      trackEvent('business_profile_modal_closed', {
        mindspace_id: defaultMindspace?.id,
        changes_made: Object.entries(formData).some(([key, value]) => 
          value.trim() !== getContextValue(key as keyof FormData)
        )
      });
      onClose();
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  const hasExistingData = contextItems.some(item => 
    ['product_service_description', 'target_audience_description', 'brand_voice'].includes(item.item_type)
  );

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal} role="dialog" aria-labelledby="business-profile-title" aria-modal="true">
        <div className={styles.header}>
          <h2 id="business-profile-title" className={styles.title}>
            Profil afacere
          </h2>
          <button
            onClick={handleClose}
            className={styles.closeButton}
            disabled={isSubmitting}
            title="Închide"
            aria-label="Închide modal"
          >
            <CloseIcon className={styles.closeIcon} title="Pictogramă închidere" />
          </button>
        </div>

        <div className={styles.content}>
          {!hasExistingData && (
            <div className={styles.emptyState}>
              <p>Nu ai completat încă detalii despre afacerea ta.</p>
              <p><strong>Actualizează datele companiei tale</strong> pentru a personaliza răspunsurile Smartie.</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className={styles.form}>
            {/* Product/Service Description */}
            <div className={styles.fieldGroup}>
              <label htmlFor="product-service" className={styles.label}>
                Produsul/Serviciul tău *
              </label>
              <textarea
                id="product-service"
                value={formData.product_service_description}
                onChange={(e) => handleInputChange('product_service_description', e.target.value)}
                className={`${styles.textarea} ${errors.product_service_description ? styles.error : ''}`}
                placeholder="Descrie pe scurt produsul sau serviciul pe care îl oferi..."
                rows={3}
                disabled={isSubmitting}
                maxLength={500}
              />
              {errors.product_service_description && (
                <span className={styles.errorText}>{errors.product_service_description}</span>
              )}
            </div>

            {/* Target Audience Description */}
            <div className={styles.fieldGroup}>
              <label htmlFor="target-audience" className={styles.label}>
                Audiența țintă *
              </label>
              <textarea
                id="target-audience"
                value={formData.target_audience_description}
                onChange={(e) => handleInputChange('target_audience_description', e.target.value)}
                className={`${styles.textarea} ${errors.target_audience_description ? styles.error : ''}`}
                placeholder="Cine sunt clienții tăi ideali? (vârstă, interese, provocări...)"
                rows={3}
                disabled={isSubmitting}
                maxLength={500}
              />
              {errors.target_audience_description && (
                <span className={styles.errorText}>{errors.target_audience_description}</span>
              )}
            </div>

            {/* Brand Voice */}
            <div className={styles.fieldGroup}>
              <label htmlFor="brand-voice" className={styles.label}>
                Vocea brandului *
              </label>
              <input
                type="text"
                id="brand-voice"
                value={formData.brand_voice}
                onChange={(e) => handleInputChange('brand_voice', e.target.value)}
                className={`${styles.input} ${errors.brand_voice ? styles.error : ''}`}
                placeholder="Ex: Prietenos și profesional, Autoritar și încrezător..."
                disabled={isSubmitting}
                maxLength={100}
              />
              {errors.brand_voice && (
                <span className={styles.errorText}>{errors.brand_voice}</span>
              )}
            </div>

            {/* General Error */}
            {(errors.general || mindspaceError) && (
              <div className={styles.generalError}>
                {errors.general || mindspaceError}
              </div>
            )}

            {/* Form Actions */}
            <div className={styles.actions}>
              <button
                type="button"
                onClick={handleClose}
                className={styles.cancelButton}
                disabled={isSubmitting}
              >
                Anulează
              </button>
              {!isGuest && (
                <button
                  type="submit"
                  className={styles.saveButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Se salvează...' : 'Salvează'}
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Success Toast */}
        {showSuccessToast && (
          <div className={styles.successToast}>
            Profilul a fost actualizat cu succes!
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessProfileModal; 